import React from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";

const METRICS = [
  ["script_readiness", "Script readiness"],
  ["character_readiness", "Characters"],
  ["scene_prep", "Scene prep"],
  ["storyboard_completion", "Storyboards"],
  ["doctor_score", "Doctor score"],
];

export default function Production() {
  const { projectId } = useParams();
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/production`);
        setData(data);
      } catch (e) { toast.error("Could not load production data"); }
    })();
  }, [projectId]);

  if (!data) return <DashboardLayout><div className="p-10 text-[#A1A1AA]">Loading production dashboard…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Production Hub</div>
        <h1 className="font-editorial text-4xl mb-8">Production readiness</h1>

        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-6">
          <div className="border border-[#1E1E1E] bg-[#141414] p-8 flex flex-col items-center justify-center text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Overall readiness</div>
            <ReadinessRing value={data.readiness_score} />
            <div className="text-xs text-[#A1A1AA] mt-4 max-w-[220px]">
              How close this project is to being ready to shoot. Improves as script, characters, scenes and storyboards mature.
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#1E1E1E]">
            {METRICS.map(([k, label]) => (
              <div key={k} className="bg-[#0A0A0A] p-4" data-testid={`prod-${k}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</div>
                <div className="mt-2 font-editorial text-3xl">{data[k]}<span className="text-sm text-[#71717A]">%</span></div>
                <div className="mt-2 h-0.5 bg-[#1E1E1E]">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${data[k]}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E1E1E]">
          {[
            ["Scenes", data.counts.scenes],
            ["Characters", data.counts.characters],
            ["Locations", data.counts.locations],
            ["Shots", `${data.counts.approved_shots}/${data.counts.shots}`],
            ["Estimated pages", data.counts.estimated_pages],
            ["Script nodes", data.counts.nodes],
          ].map(([label, v]) => (
            <div key={label} className="bg-[#0A0A0A] p-5">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</div>
              <div className="mt-2 font-editorial text-3xl">{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-[#1E1E1E] bg-[#0F0F10] p-8">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Budget · schedule · casting</div>
          <div className="font-editorial text-xl">Coming next.</div>
          <p className="text-sm text-[#A1A1AA] mt-2 max-w-lg">
            Budgeting, shooting schedule and casting boards are on the immediate roadmap. Every entity in your project — scenes, characters, locations — is already structured to feed them.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ReadinessRing({ value }) {
  const r = 60, c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  return (
    <div className="relative w-40 h-40 mt-4">
      <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
        <circle cx="70" cy="70" r={r} stroke="#1E1E1E" strokeWidth="6" fill="none" />
        <circle cx="70" cy="70" r={r} stroke="#D4AF37" strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="butt" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-editorial text-5xl text-[#D4AF37]" data-testid="readiness-score">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">of 100</div>
      </div>
    </div>
  );
}
